// [2024-05-08] Store 리듀서 구현
import { Store } from "./types";
import { Action } from "./actions";
import { AnyAction } from "@reduxjs/toolkit";
import { AIReview } from "common/models/types";
import { groupPrescriptions } from "utils/PrescriptionUtils";
import { getDateStringFromToday } from "utils/TimeUtil";
import { fetchCheckReferenceBatch } from "./middleware";

const initialState: Store = {
  patientList: null,
  patients: {},
  references: {},
  reviews: {},
  reviewHistory: null,
  pinnedReviews: null,
  aiReviewCount: null,
  pinnedReviewCount: null,
  nhisRecentRequests: null,
  referenceChecker: {},
  drugLabels: {},
};

export const reducer = (state = initialState, action: AnyAction): Store => {
  switch (action.type) {
    case "SET_PATIENT_LIST":
      return {
        ...state,
        patientList: action.payload,
      };

    case "SET_PATIENT":
      return {
        ...state,
        patients: {
          ...state.patients,
          [action.payload.id]: {
            ...(state.patients[action.payload.id] || {}),
            ...action.payload,
          },
        },
      };

    case "SET_PATIENT_REVIEWS":
      return {
        ...state,
        patients: {
          ...state.patients,
          [action.payload.patientId]: {
            ...(state.patients[action.payload.patientId] || {}),
            aiReviews: action.payload.reviews.map((review) => ({ id: review.id })),
          },
        },
      };

    case "TOUCH_PATIENT_UPDATED_AT": {
      const pid = action.payload;
      if (!state.patients[pid]) return state;
      return {
        ...state,
        patients: {
          ...state.patients,
          [pid]: {
            ...state.patients[pid],
            updatedAt: getDateStringFromToday() as unknown as Date,
          },
        },
      };
    }

    case "REMOVE_PATIENT": {
      const { [action.payload]: removed, ...remaining } = state.patients;
      return {
        ...state,
        patientList: state.patientList?.filter((patient) => patient.id !== action.payload) ?? null,
        patients: remaining,
      };
    }

    case "SET_REVIEW":
      const existingReview = state.reviews[action.payload.id];
      let newReview: AIReview = {
        ...(existingReview || {}),
        ...action.payload,
      };

      if (action.payload?.patientSnap?.prescriptions) {
        const rebuiltGroups = groupPrescriptions(
          action.payload.patientSnap.prescriptions
        );
        // 재조립은 개별 처방 필드만 보므로 백엔드 스냅샷 그룹의 hospitalAlias/hospitalLabel이
        // 소실된다 — alias는 병원명 단위로 부여되므로(같은 병원명 = 같은 alias) 병원명으로 재부착.
        // 이게 없으면 뷰어의 aliasMap이 비어 ⑆의료기관A 마커가 버튼 대신 평문 잔해로 남는다.
        const snapGroups = action.payload.patientSnap.prescriptionGroups;
        if (Array.isArray(snapGroups)) {
          const aliasByHospital = new Map<string, { hospitalAlias?: string; hospitalLabel?: string }>();
          for (const g of snapGroups) {
            if (g?.hospital && g?.hospitalAlias && !aliasByHospital.has(g.hospital)) {
              aliasByHospital.set(g.hospital, { hospitalAlias: g.hospitalAlias, hospitalLabel: g.hospitalLabel });
            }
          }
          for (const g of rebuiltGroups) {
            const src = g.hospital ? aliasByHospital.get(g.hospital) : undefined;
            if (src) {
              g.hospitalAlias = src.hospitalAlias;
              g.hospitalLabel = src.hospitalLabel;
            }
          }
        }
        newReview.patientSnap.prescriptionGroups = rebuiltGroups;
      }

      // 기존 review와 동일한지 확인 (얕은 비교)
      if (existingReview) {
        let hasChanged = false;

        for (const key in newReview) {
          if (newReview[key as keyof AIReview] !== existingReview[key as keyof AIReview]) {
            hasChanged = true;
            break; // 첫 번째 변경 발견 시 즉시 종료
          }
        }

        // 변경사항이 없으면 기존 객체 재사용
        if (!hasChanged) {
          return state;
        }
      }

      return {
        ...state,
        reviews: {
          ...state.reviews,
          [action.payload.id]: newReview,
        },
      };

    case "SET_LATEST_REVIEWS":
      const reviews = action.payload;
      const newReviews = { ...state.reviews };

      const reviewHistory = reviews.map((review) => {
        newReviews[review.id] = {
          ...(newReviews[review.id] || {}),
          ...review,
        };
        return { id: review.id };
      });

      return {
        ...state,
        reviewHistory,
        reviews: newReviews,
      };

    case "SET_PINNED_REVIEWS": {
      const pinnedPayload = action.payload as AIReview[];
      const updatedReviews = { ...state.reviews };

      const pinnedReviews = pinnedPayload.map((review) => {
        updatedReviews[review.id] = {
          ...(updatedReviews[review.id] || {}),
          ...review,
        };
        return { id: review.id };
      });

      return {
        ...state,
        pinnedReviews,
        reviews: updatedReviews,
      };
    }

    case "SET_AI_REVIEW_COUNT":
      return {
        ...state,
        aiReviewCount: action.payload,
      };

    case "SET_PINNED_REVIEW_COUNT":
      return {
        ...state,
        pinnedReviewCount: action.payload,
      };

    case "SET_NHIS_RECENT_REQUESTS":
      return {
        ...state,
        nhisRecentRequests: action.payload,
      };

    case "SET_REFERENCE_CHECKER":
      return {
        ...state,
        referenceChecker: {
          ...state.referenceChecker,
          [action.payload.itemCode]: {
            count: action.payload.count,
            totalLength: action.payload.totalLength || 0,
            totalBytes: action.payload.totalBytes || 0,
          },
        },
      };

    case "SET_DRUG_LABELS":
      return {
        ...state,
        drugLabels: {
          ...state.drugLabels,
          [action.payload.itemCode]: action.payload.labels,
        },
      };

    case "REMOVE_REVIEW": {
      const { [action.payload]: removed, ...remaining } = state.reviews;
      return {
        ...state,
        reviews: remaining,
      };
    }

    case "SET_REFERENCE":
      return {
        ...state,
        references: {
          ...state.references,
          [action.payload.id]: {
            ...(state.references[action.payload.id] || {}),
            ...action.payload,
          },
        },
      };

    case "REMOVE_REFERENCE": {
      const { [action.payload]: removed, ...remaining } = state.references;
      return {
        ...state,
        references: remaining,
      };
    }

    // fetchCheckReferenceBatch의 pending 액션 처리
    case fetchCheckReferenceBatch.pending.type: {
      const itemCodes = action.meta.arg as string[];
      const updates: { [key: string]: { count: number; totalLength: number; totalBytes?: number } } = {};
      
      itemCodes.forEach(itemCode => {
        if (itemCode && state.referenceChecker[itemCode] === undefined) {
          updates[itemCode] = { count: -1, totalLength: 0, totalBytes: 0 };
        }
      });
      
      return {
        ...state,
        referenceChecker: {
          ...state.referenceChecker,
          ...updates
        }
      };
    }

    default:
      return state;
  }
};
